<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    computed,
    type Ref,
    ref,
    defineEmits,
    defineProps,
    onMounted,
    onUnmounted,
    reactive,
    watch,
    type PropType
  } from 'vue'
  import type {
    CustomerCase as Case,
    CustomerEntityChange,
    CustomerEntityWithRelationship as EntityWithRelationship,
    Entity as EntityType,
    EntityRelationship,
    GenericDataMapper
  } from '@/types/generated'
  import type { CustomExportValidation } from '@/views/integrations/ExportView.vue'
  import {
    RelationshipTypeEnum,
    EntitiesMapperKeyEnum,
    EntityTypeEnum,
    CompanyLegalFormEnum,
    BlankEnum,
    TitleEnum
  } from '@/types/generated'
  import { EntitiesService } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import DatamapperSource from '@/components/app/cases/DatamapperSource.vue'
  import DatamapperTable from '@/components/app/cases/DatamapperTable.vue'
  import DataMapperTimeTravel from '@/components/app/cases/DataMapperTimeTravel.vue'
  import LegalFormDuplicateWarning from '@/components/app/cases/LegalFormDuplicateWarning.vue'
  import RelationshipReplacementModal from '@/components/app/cases/entities/RelationshipReplacementModal.vue'
  import { Button } from '@/components/ui/button'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList
  } from '@/components/ui/command'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import { debounce } from '@/lib/timing'
  import { yup } from '@/lib/validation'
  import { Building2, ChevronsUpDown, Landmark, Search, User, X } from 'lucide-vue-next'

  const { t } = useTranslation({
    en: {
      personalInfo: 'Personal Information',
      companyInfo: 'Company Information',
      institutionInfo: 'Institution Information',
      contactInfo: 'Contact Info',
      address: 'Address',
      bankingInfo: 'Banking Information',
      legalInsurance: 'Legal Insurance',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution',
      type: 'Type',
      role: 'Role',
      selectRelationshipTypes: 'Relationship types...',
      noAvailableRelationshipTypes: 'No available relationship types',
      noOptionsFound: 'No options found.',
      searchRelationTypes: 'Search relation types...',
      deleteEntity: 'Delete Entity',
      deleteEntityConfirmation: 'Are you sure you want to delete this entity?',
      notEnabledInExport: 'Not enabled in this export',
      entityDetails: 'Information',
      ibanValidationError: 'Please enter a valid IBAN.',
      bicValidationError: 'Please enter a valid BIC.',
      dateFormatError: 'Please enter a valid date in DD.MM.YYYY format',
      phoneMinLengthError: 'Phone number must be empty or at least 3 characters long',
      cancel: 'Cancel',
      delete: 'Delete',
      ...Object.fromEntries(Object.entries(entitiesEN).map(([key, value]) => [key, value]))
    },
    de: {
      personalInfo: 'Persönliche Informationen',
      companyInfo: 'Unternehmen',
      institutionInfo: 'Institution',
      contactInfo: 'Ansprechpartner',
      address: 'Adresse',
      bankingInfo: 'Bankverbindung',
      legalInsurance: 'Rechtsschutzversicherung',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution',
      type: 'Typ',
      role: 'Rolle',
      selectRelationshipTypes: 'Beziehungstypen...',
      noAvailableRelationshipTypes: 'Keine verfügbaren Beziehungstypen',
      noOptionsFound: 'Keine Optionen gefunden.',
      searchRelationTypes: 'Beziehungstypen suchen...',
      deleteEntity: 'Beteiligte löschen',
      deleteEntityConfirmation: 'Sind Sie sicher, dass Sie diese Beteiligte löschen möchten?',
      notEnabledInExport: 'In diesem Export nicht aktiviert',
      entityDetails: 'Informationen',
      ibanValidationError: 'Bitte geben Sie eine gültige IBAN ein.',
      bicValidationError: 'Bitte geben Sie eine gültige BIC ein.',
      dateFormatError: 'Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein',
      phoneMinLengthError: 'Die Telefonnummer muss leer sein oder mindestens 3 Zeichen enthalten',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      ...Object.fromEntries(Object.entries(entitiesDE).map(([key, value]) => [key, value]))
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  // Extract mandatory keys from Yup validation schemas
  const getMandatoryKeysFromValidation = (
    validationSchema: Record<string, yup.AnySchema> | null
  ): string[] => {
    if (!validationSchema) return []

    const mandatoryKeys: string[] = []

    Object.entries(validationSchema).forEach(([key, schema]) => {
      try {
        // Check if the schema is required by testing with undefined
        schema.validateSync(undefined)
      } catch (error: any) {
        // If validation fails with undefined, it means the field is required
        if (error?.type === 'required' || error?.message?.includes('required')) {
          mandatoryKeys.push(key)
        }
      }
    })

    return mandatoryKeys
  }

  const entityTypeAwareValidation = computed(() => {
    const validationConfig = props.customExportValidation || {}
    const baseValidation = validationConfig.baseValidation || {}
    const entityTypeValidation = validationConfig.entityTypeValidation || {}
    const entityType = entityRelationShip.value?.entity.entity_type

    let mergedValidation = { ...baseValidation }

    // Add entity type-specific validation if it exists for this entity type
    if (entityType && entityTypeValidation[entityType]) {
      mergedValidation = {
        ...mergedValidation,
        ...entityTypeValidation[entityType]
      }
    }

    return mergedValidation
  })

  const dynamicMandatoryKeys = computed(() =>
    getMandatoryKeysFromValidation(entityTypeAwareValidation.value)
  )

  const relationshipTypeOptions = computed<Option[]>(() => {
    return Object.values(RelationshipTypeEnum).map((value) => ({
      value,
      title: t(value)
    }))
  })

  const props = defineProps({
    entityRelationshipUuid: {
      type: String,
      required: true
    },
    insuranceInfoEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    customExportValidation: {
      type: Object as PropType<{
        baseValidation?: Record<string, yup.AnySchema>
        entityTypeValidation?: Record<string, Record<string, yup.AnySchema>>
      } | null>,
      required: false,
      default: () => ({})
    }
  })

  const entityRelationShip = computed<EntityWithRelationship>(() => {
    return caseRecord.value?.data.entities.find(
      (entity) => entity.uuid === props.entityRelationshipUuid
    ) as EntityWithRelationship
  })

  // State for relationship types selection
  const selectedRelationshipTypes = ref<string[]>([])
  const relationshipFilter = ref('')

  // State for relationship replacement modal
  const showRelationshipModal = ref(false)
  const modalAction = ref<'add' | 'remove'>('add')
  const modalRelationshipType = ref<RelationshipTypeEnum>(RelationshipTypeEnum.ENTITIES_CLIENT)
  const modalRelationshipUuid = ref<string>('')

  // State for delete confirmation modal
  const showDeleteConfirmation = ref(false)

  const availableRelationshipTypes = computed<RelationshipTypeEnum[]>(() => {
    if (!entityRelationShip.value || !caseRecord.value?.data) return []

    // Get relationships specific to this entity
    const existingTypesForEntity = new Set(
      entityRelationShip.value.relationships.map((r) => r.relationship_type)
    )

    const allTypes = Object.values(RelationshipTypeEnum)

    return allTypes.filter((type) => {
      const existsForEntity = existingTypesForEntity.has(type)

      // If this entity already has this type of relationship, filter it out
      if (existsForEntity) return false

      return true
    })
  })

  const addRelationship = async (type: RelationshipTypeEnum, relationshipUuid: string) => {
    if (
      type === RelationshipTypeEnum.ENTITIES_CLIENT ||
      type === RelationshipTypeEnum.ENTITIES_OPPONENT
    ) {
      // Only one client and one opponent per case -> need to show modal to resolve this
      modalAction.value = 'add'
      modalRelationshipType.value = type
      modalRelationshipUuid.value = relationshipUuid
      showRelationshipModal.value = true
      // Close the popover modal to not interfere with the modal
      isOpen.value = false
      return
    }
    const response = await EntitiesService.entitiesAddCaseDataRelationshipCreate({
      uuid: entityRelationShip.value.entity.uuid,
      requestBody: {
        case_data_uuid: caseRecord.value!.data.uuid as string,
        relationship_type: type
      }
    })
    entityRelationShip.value.relationships = response.relationships
    // Close the popover after adding a relationship
    isOpen.value = false
  }

  const removeRelationship = async (type: RelationshipTypeEnum, relationshipUuid: string) => {
    if (!entityRelationShip.value) return
    if (
      type === RelationshipTypeEnum.ENTITIES_CLIENT ||
      type === RelationshipTypeEnum.ENTITIES_OPPONENT
    ) {
      // Only one client and one opponent per case -> need to show modal to resolve this
      modalAction.value = 'remove'
      modalRelationshipType.value = type
      modalRelationshipUuid.value = relationshipUuid
      // Use setTimeout to ensure the modal opens after the popover is closed
      setTimeout(() => {
        showRelationshipModal.value = true
      }, 50)
      return
    }
    const response = await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
      uuid: entityRelationShip.value.entity.uuid,
      requestBody: {
        case_data_uuid: caseRecord.value!.data.uuid as string,
        relationship_uuid: relationshipUuid,
        remove_case_data_association_if_no_relationships_exist: false
      }
    })

    if (response.relationship !== null) {
      entityRelationShip.value.relationships = response.relationship.relationships
    }
  }

  const handleRelationshipUpdated = async () => {
    // Refresh the case data to get the updated relationships
    if (caseStore.caseRecord?.uuid) {
      await caseStore.loadCase(caseStore.caseRecord.uuid)
    }
  }

  const handleEntityTypeChange = async (type: string) => {
    if (!entityRelationShip.value) return
    entityRelationShip.value.entity.entity_type = type as EntityTypeEnum
    resetErrorStatesAndTotalNumberOfErrors()
    await handleSave('entity_type', EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE, type)
  }

  const handleSave = debounce(async (key: string, mapperKey: string, value: any) => {
    if (!entityRelationShip.value) return
    console.log('Saving entity:', key, mapperKey, value)
    const response = await EntitiesService.entitiesPartialUpdate({
      uuid: entityRelationShip.value.entity?.uuid || '',
      requestBody: {
        [key]: value
      }
    })
    // Update the entity with the full response data, not just changes
    entityRelationShip.value.entity = {
      ...entityRelationShip.value.entity,
      ...response,
      changes: response.changes as CustomerEntityChange[]
    }
  }, 1000)

  const onValueChange = async (key: string, mapperKey: string, value: any) => {
    // Update the store directly
    caseStore.updateEntity(entityRelationShip.value.entity?.uuid || '', {
      [key]: value
    })
    await handleSave(key, mapperKey, value)
  }

  const totalNumberOfErrors = ref(0)
  const errorStates = reactive<{ [key: string]: boolean }>({})

  // Reset errorStates and totalNumberOfErrors when entity changes

  const resetErrorStatesAndTotalNumberOfErrors = () => {
    Object.keys(errorStates).forEach((key) => delete errorStates[key])
    totalNumberOfErrors.value = 0
  }
  watch(() => props.entityRelationshipUuid, resetErrorStatesAndTotalNumberOfErrors)

  const handleValueChangeError = (mapperKey: string, isError: boolean) => {
    errorStates[mapperKey] = isError
    totalNumberOfErrors.value = Object.values(errorStates).filter(Boolean).length
    emit('totalNumberOfErrors', entityRelationShip.value.entity.uuid, totalNumberOfErrors.value)
  }
  const emit = defineEmits(['totalNumberOfErrors'])

  const isClient = computed<boolean>(() => {
    return !!entityRelationShip.value?.relationships.some(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  })

  // Define court-related relationship types
  const COURT_RELATIONSHIP_TYPES = [
    RelationshipTypeEnum.ENTITIES_COURT,
    RelationshipTypeEnum.ENTITIES_FIRST_INSTANCE_COURT,
    RelationshipTypeEnum.ENTITIES_SECOND_INSTANCE_COURT,
    RelationshipTypeEnum.ENTITIES_THIRD_INSTANCE_COURT,
    RelationshipTypeEnum.ENTITIES_DUNNING_COURT,
    RelationshipTypeEnum.ENTITIES_ENFORCEMENT_COURT,
    RelationshipTypeEnum.ENTITIES_REGISTER_COURT,
    RelationshipTypeEnum.ENTITIES_PROBATE_COURT,
    RelationshipTypeEnum.ENTITIES_LEGAL_AID_COURT,
    RelationshipTypeEnum.ENTITIES_INSOLVENCY_COURT
  ]

  const isCourt = computed<boolean>(() => {
    return !!entityRelationShip.value?.relationships.some((r) =>
      COURT_RELATIONSHIP_TYPES.includes(r.relationship_type as RelationshipTypeEnum)
    )
  })

  // Computed properties for conditional validation
  const personalInfoMandatoryKeys = computed(() => {
    // For courts, don't require last_name
    if (isCourt.value) {
      return []
    }
    // For non-courts, require last_name
    return [EntitiesMapperKeyEnum.ENTITIES_FIELD_LAST_NAME]
  })

  const personalInfoCustomValidators = computed(() => {
    const validators: Record<string, any> = {
      [EntitiesMapperKeyEnum.ENTITIES_FIELD_DATE_OF_BIRTH]: yup
        .string()
        .nullable()
        .matches(
          /^$|^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
          t('dateFormatError')
        ),
      [EntitiesMapperKeyEnum.ENTITIES_FIELD_PHONE]: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          'empty-or-min-3',
          t('phoneMinLengthError'),
          (value) => value === '' || value == null || value.length >= 3
        )
    }

    // For courts, make last_name optional
    if (isCourt.value) {
      validators[EntitiesMapperKeyEnum.ENTITIES_FIELD_LAST_NAME] = yup
        .string()
        .nullable()
        .notRequired()
    } else {
      // For non-courts, require last_name
      validators[EntitiesMapperKeyEnum.ENTITIES_FIELD_LAST_NAME] = yup
        .string()
        .nonNullable()
        .required()
    }

    return validators
  })

  const bankingCustomValidators = computed(() => {
    return {
      [EntitiesMapperKeyEnum.ENTITIES_FIELD_IBAN]: yup
        .string()
        .nullable()
        .notRequired()
        .iban(t('ibanValidationError')),
      [EntitiesMapperKeyEnum.ENTITIES_FIELD_BIC]: yup
        .string()
        .nullable()
        .notRequired()
        .bic(t('bicValidationError'))
    }
  })

  const entityTypeChanges = computed<CustomerEntityChange[]>(() => {
    if (!entityRelationShip.value) return []
    return (
      entityRelationShip.value.entity.changes?.filter(
        (change) => change.entities_mapper_key === EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE
      ) || []
    )
  })

  const latestEntityTypeChange = computed<CustomerEntityChange | null>(() => {
    if (!entityRelationShip.value) return null
    return entityRelationShip.value.entity.changes?.find(
      (change) => change.entities_mapper_key === EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE
    ) as CustomerEntityChange | null
  })

  // Control for the Popover open state
  const isOpen = ref(false)

  // Filtered options based on search input
  const filteredOptions = computed<Option[]>(() => {
    // Create all options with translated titles
    const allOptions = availableRelationshipTypes.value
      .map((value) => ({
        value,
        title: t(value)
      }))
      .sort((a, b) => a.title.localeCompare(b.title))

    // If no filter, return all options
    if (!relationshipFilter.value || relationshipFilter.value.trim() === '') {
      return allOptions
    }

    // Filter by the title text (case insensitive)
    const searchTerm = relationshipFilter.value.toLowerCase().trim()
    return allOptions.filter((option) => option.title.toLowerCase().includes(searchTerm))
  })

  // Function to handle entity deletion
  const deleteEntity = () => {
    if (!entityRelationShip.value || !entityRelationShip.value.entity.uuid) return

    // Show the delete confirmation modal
    showDeleteConfirmation.value = true
  }

  // Function to confirm entity deletion
  const confirmDeleteEntity = async () => {
    if (!entityRelationShip.value || !entityRelationShip.value.entity.uuid || !caseRecord.value)
      return

    try {
      await caseStore.deleteEntity(entityRelationShip.value)

      showDeleteConfirmation.value = false
    } catch (error) {
      console.error('Error deleting entity:', error)
    }
  }

  const handleFixCompanyName = async () => {
    if (!entityRelationShip.value) return

    const companyName = entityRelationShip.value.entity.company_name || ''
    const legalForm = entityRelationShip.value.entity.company_legal_form || ''

    if (!companyName || !legalForm) return

    const pattern = new RegExp(
      `[\\s,\\-]*${legalForm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      'i'
    )
    const cleanedName = companyName.replace(pattern, '').trim()

    if (cleanedName) {
      await onValueChange(
        'company_name',
        EntitiesMapperKeyEnum.ENTITIES_FIELD_COMPANY_NAME,
        cleanedName
      )
    }
  }
</script>

<template>
  <div class="w-full">
    <div class="mb-4">
      <div class="mb-4 font-semibold text-gray-800">
        {{ t('entityDetails') }}
      </div>
      <Table
        class="w-full table-fixed [&>div]:!border-0 [&_table]:border-separate [&_td]:!border-0 [&_th]:!border-0
          [&_tr:nth-child(even)]:bg-slate-50 [&_tr]:!border-0"
      >
        <TableBody>
          <TableRow>
            <TableCell class="!h-6 w-[15%] p-3 font-medium">{{ t('type') }}</TableCell>
            <TableCell class="!h-6 w-[30%] p-3">
              <SelectField
                name="entity_type"
                :options="[
                  {
                    value: EntityTypeEnum.NATURAL_PERSON,
                    title: t('naturalPerson'),
                    icon: User
                  },
                  {
                    value: EntityTypeEnum.LEGAL_PERSON,
                    title: t('legalPerson'),
                    icon: Building2
                  },
                  {
                    value: EntityTypeEnum.INSTITUTION,
                    title: t('institution'),
                    icon: Landmark
                  }
                ]"
                :model-value="entityRelationShip?.entity?.entity_type"
                @update:model-value="handleEntityTypeChange"
              />
            </TableCell>
            <TableCell class="!h-6 w-[30%] p-3">
              <DatamapperSource :change="latestEntityTypeChange" />
            </TableCell>
            <TableCell class="!h-6 w-[15%] p-3">
              <DataMapperTimeTravel
                :show-time-travel="true"
                :latest-change="latestEntityTypeChange"
                :changes="entityTypeChanges"
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell class="!h-6 w-[15%] p-3 font-medium">{{ t('role') }}</TableCell>
            <TableCell class="!h-6 w-[30%] p-3">
              <div class="w-full">
                <Popover v-model:open="isOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      role="combobox"
                      :disabled="availableRelationshipTypes.length === 0"
                      :class="[
                        'h-auto min-h-[40px] w-full justify-between border border-gray-200 text-left font-normal'
                      ]"
                    >
                      <div class="flex flex-wrap items-center gap-1 truncate">
                        <!-- Selected items as badges -->
                        <div
                          v-for="relationship in entityRelationShip.relationships"
                          :key="relationship.uuid"
                          class="flex items-center overflow-hidden rounded-full border border-gray-200 bg-white px-2 py-1 text-sm
                            text-gray-800"
                        >
                          <div class="truncate">
                            {{
                              relationshipTypeOptions.find(
                                (option) => option.value === relationship.relationship_type
                              )?.title
                            }}
                          </div>
                          <button
                            type="button"
                            class="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-800 hover:bg-gray-300
                              focus:outline-none"
                            @click.stop.prevent="
                              removeRelationship(
                                relationship.relationship_type,
                                relationship.uuid as string
                              )
                            "
                          >
                            <X class="h-3 w-3" />
                          </button>
                        </div>

                        <!-- Placeholder when no selections but there are options available -->
                        <span
                          v-if="
                            entityRelationShip.relationships.length === 0 &&
                            availableRelationshipTypes.length > 0
                          "
                          class="text-sm text-gray-500"
                        >
                          {{ t('selectRelationshipTypes') }}
                        </span>
                        <!-- When there are no available options -->
                        <span
                          v-if="
                            entityRelationShip.relationships.length === 0 &&
                            availableRelationshipTypes.length === 0
                          "
                          class="text-sm text-gray-500"
                        >
                          {{ t('noAvailableRelationshipTypes') }}
                        </span>
                      </div>
                      <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command>
                      <div
                        class="flex items-center border-b px-3"
                        cmdk-input-wrapper=""
                      >
                        <Search class="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          v-model="relationshipFilter"
                          type="text"
                          :placeholder="t('searchRelationTypes')"
                          class="flex h-9 w-full rounded-md border-0 border-none bg-transparent py-3 text-sm shadow-none outline-none
                            ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:cursor-not-allowed
                            disabled:opacity-50"
                          aria-activedescendant="radix-vue-combobox-option-v-353"
                        />
                      </div>
                      <CommandEmpty v-if="filteredOptions.length === 0">
                        {{ t('noOptionsFound') }}
                      </CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          <CommandItem
                            v-for="option in filteredOptions"
                            :key="'relationship-type-' + option.value"
                            :value="option.title"
                            class="cursor-pointer"
                            @select="
                              addRelationship(
                                option.value as RelationshipTypeEnum,
                                entityRelationShip.uuid || ''
                              )
                            "
                          >
                            <div class="flex items-center">
                              {{ option.title }}
                            </div>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </TableCell>
            <TableCell class="!h-6 w-[30%] p-3"></TableCell>
            <TableCell class="!h-6 w-[15%] p-3"></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>

    <div
      v-if="entityRelationShip.entity.entity_type === EntityTypeEnum.LEGAL_PERSON"
      class="mb-4"
    >
      <div class="mb-4 font-semibold text-gray-800">
        {{ t('companyInfo') }}
      </div>
      <DatamapperTable
        :show-keys="[
          EntitiesMapperKeyEnum.ENTITIES_FIELD_COMPANY_NAME,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_COMPANY_LEGAL_FORM
        ]"
        :mandatory-keys="dynamicMandatoryKeys"
        :custom-validators="entityTypeAwareValidation"
        :object="entityRelationShip"
        :is-entity="true"
        :sorting="false"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="'edit'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
      <LegalFormDuplicateWarning
        :company-name="entityRelationShip.entity.company_name || ''"
        :legal-form="entityRelationShip.entity.company_legal_form || ''"
        :on-fix="handleFixCompanyName"
      />
    </div>
    <div
      v-if="entityRelationShip.entity.entity_type === EntityTypeEnum.INSTITUTION"
      class="mb-4"
    >
      <div class="mb-4 font-semibold text-gray-800">
        {{ t('institutionInfo') }}
      </div>
      <DatamapperTable
        :show-keys="[EntitiesMapperKeyEnum.ENTITIES_FIELD_INSTITUTION_NAME]"
        :mandatory-keys="dynamicMandatoryKeys"
        :custom-validators="entityTypeAwareValidation"
        :object="entityRelationShip"
        :is-entity="true"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="'edit'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </div>
    <div class="mb-4">
      <div class="mb-4 font-semibold text-gray-800">
        {{
          entityRelationShip.entity.entity_type === EntityTypeEnum.NATURAL_PERSON
            ? t('personalInfo')
            : t('contactInfo')
        }}
      </div>
      <DatamapperTable
        :show-keys="[
          EntitiesMapperKeyEnum.ENTITIES_FIELD_TITLE,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_SPECIFIC_TITLE,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_FIRST_NAME,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_LAST_NAME,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_EMAIL,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_PHONE,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_DATE_OF_BIRTH
        ]"
        :mandatory-keys="dynamicMandatoryKeys"
        :custom-validators="entityTypeAwareValidation"
        :object="entityRelationShip"
        :is-entity="true"
        :sorting="false"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="'edit'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </div>
    <div class="mb-4">
      <div class="mb-4 font-semibold text-gray-800">
        {{ t('address') }}
      </div>
      <DatamapperTable
        :show-keys="[
          EntitiesMapperKeyEnum.ENTITIES_FIELD_STREET,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_STREET_NUMBER,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_ZIP,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_CITY,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_ADDRESS_LINE_2
        ]"
        :mandatory-keys="dynamicMandatoryKeys"
        :custom-validators="entityTypeAwareValidation"
        :object="entityRelationShip"
        :is-entity="true"
        :sorting="false"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="'edit'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </div>
    <div class="mb-4">
      <div class="mb-4 font-semibold text-gray-800">
        {{ t('bankingInfo') }}
      </div>
      <DatamapperTable
        :show-keys="[
          EntitiesMapperKeyEnum.ENTITIES_FIELD_IBAN,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_BIC,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_BANK_NAME
        ]"
        :custom-validators="bankingCustomValidators as any"
        :object="entityRelationShip"
        :is-entity="true"
        :sorting="false"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="'edit'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </div>
    <div
      v-if="isClient"
      class="mb-4"
    >
      <div
        class="mb-4 font-semibold"
        :class="{ 'text-gray-400': !insuranceInfoEnabled, 'text-gray-800': insuranceInfoEnabled }"
      >
        {{ t('legalInsurance') }}
        <span v-if="!insuranceInfoEnabled">({{ t('notEnabledInExport') }})</span>
      </div>
      <DatamapperTable
        :show-keys="[
          EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE_NAME,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE_NUMBER,
          EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE_DEDUCTIBLE_AMOUNT
        ]"
        :mandatory-keys="dynamicMandatoryKeys"
        :custom-validators="entityTypeAwareValidation"
        :object="entityRelationShip"
        :is-entity="true"
        :sorting="false"
        :mappers="caseRecord?.data.mappers as GenericDataMapper[]"
        :mode="insuranceInfoEnabled ? 'edit' : 'view'"
        :ghost="true"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </div>
  </div>

  <!-- Relationship Replacement Modal -->
  <RelationshipReplacementModal
    v-model:isOpen="showRelationshipModal"
    :action="modalAction"
    :relationshipType="modalRelationshipType"
    :relationshipUuid="modalRelationshipUuid"
    @relationship-updated="handleRelationshipUpdated"
  />

  <!-- Delete button at the bottom - hidden for clients and opponents -->
  <div
    v-if="
      !entityRelationShip.relationships.some(
        (r) =>
          r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT ||
          r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    "
    class="mt-8 flex justify-end"
  >
    <Button
      variant="destructive"
      class="bg-red-600 hover:bg-red-700"
      @click="deleteEntity"
    >
      {{ t('deleteEntity') }}
    </Button>
  </div>

  <!-- Delete Confirmation Dialog -->
  <Dialog v-model:open="showDeleteConfirmation">
    <DialogContent>
      <DialogDescription></DialogDescription>
      <DialogHeader>
        <DialogTitle>{{ t('deleteEntity') }}</DialogTitle>
        <DialogDescription>{{ t('deleteEntityConfirmation') }}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="outline"
          @click="showDeleteConfirmation = false"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          @click="confirmDeleteEntity"
        >
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
